import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import type { VariantProps } from 'cva'
import { cva, cx } from 'cva'
import type { ComponentProps, FC } from 'react'
import { FontAwesomeIcon } from './FontAwesomeIcon'

const whitePrimaryClasses = cx(
	'bg-blue-shade-40 text-white',
	'before:bg-blue',
	'active:bg-blue-shade-70',
)
const bluePrimaryClasses = cx(
	'bg-blue-tint-20 text-blue',
	'before:bg-white hover:text-blue-shade-70',
	'active:bg-blue-shade-70 active:text-white',
)

export const buttonVariants = cva({
	base: cx(
		'text-button py-2.75 relative overflow-hidden flex items-center justify-center font-semibold rounded-md w-fit cursor-pointer pointer-events-auto',
		'before:content-[""] before:absolute before:inset-0 before:transition-all before:duration-250 before:ease-in-button translate-y-0',
		'hover:before:-translate-y-full',
	),
	variants: {
		intent: {
			primary: null,
		},
		theme: {
			white: null,
			blue: null,
		},
		hasIcon: {
			true: null,
			false: null,
		},
	},
	compoundVariants: [
		//  With Icon
		{
			theme: 'white',
			intent: 'primary',
			hasIcon: true,
			class: cx('gap-x-md pl-4.5 pr-3.5', whitePrimaryClasses),
		},
		{
			theme: 'blue',
			intent: 'primary',
			hasIcon: true,
			class: cx('gap-x-md pl-4.5 pr-3.5', bluePrimaryClasses),
		},
		//  Without Icon
		{
			theme: 'white',
			intent: 'primary',
			hasIcon: false,
			class: cx('px-4.5', whitePrimaryClasses),
		},
		{
			theme: 'blue',
			intent: 'primary',
			hasIcon: false,
			class: cx('px-3.5', bluePrimaryClasses),
		},
	],
	defaultVariants: {
		theme: 'white',
		intent: 'primary',
		hasIcon: false,
	},
})
export type ButtonVariants = VariantProps<typeof buttonVariants>

export type ButtonProps = Omit<ComponentProps<'button'>, 'children'> &
	Omit<ButtonVariants, 'hasIcon'> & {
		label: string
		icon?: [IconPrefix, IconName]
	}

export const Button: FC<ButtonProps> = ({
	intent,
	theme,
	label,
	title,
	icon,
	className,
	...props
}) => {
	const hasIcon = icon !== undefined
	return (
		<button
			{...props}
			title={title || label}
			className={buttonVariants({ intent, theme, hasIcon, className })}
		>
			<span className="relative z-10">{label}</span>
			{icon && <FontAwesomeIcon icon={icon} size="md" className="relative z-10" />}
		</button>
	)
}
